/**
 * Direct HTML video attachment for Web Zego MediaStreams.
 */

const WRAPPER_MEDIA_STREAM_FIELDS = [
  "stream",
  "mediaStream",
  "localStream",
  "videoCaptureStream",
  "audioCaptureStream",
  "zegoStream",
  "rawStream",
  "webrtcStream",
  "videoStream",
];

function isBrowserMediaStream(value) {
  return typeof MediaStream !== "undefined" && value instanceof MediaStream;
}

function describeStreamShape(value) {
  if (value == null) return String(value);
  const type = value?.constructor?.name || typeof value;
  let keys = "";
  try {
    keys = Object.keys(value).slice(0, 16).join(",");
  } catch (_) {
    keys = "unavailable";
  }
  return `type=${type} keys=${keys}`;
}

/**
 * Resolve a native MediaStream for video.srcObject from a Zego wrapper or MediaStream.
 */
export function resolveMediaStreamForVideoAttach(source, scope = "remote") {
  if (!source) {
    return { mediaStream: null, sourceField: null };
  }

  if (isBrowserMediaStream(source)) {
    if (scope === "local") {
      console.log(
        `[webZego] local attach stream type=${source?.constructor?.name || "MediaStream"}`
      );
      console.log("[webZego] local attach stream instanceof MediaStream=true");
    }
    return { mediaStream: source, sourceField: "direct" };
  }

  const streamType = source?.constructor?.name || typeof source;
  if (scope === "local") {
    console.log(`[webZego] local attach stream type=${streamType}`);
    console.log("[webZego] local attach stream instanceof MediaStream=false");
  }

  for (const field of WRAPPER_MEDIA_STREAM_FIELDS) {
    let candidate = null;
    try {
      candidate = source?.[field];
    } catch (_) {
      candidate = null;
    }
    if (isBrowserMediaStream(candidate)) {
      if (scope === "local") {
        console.log(`[webZego] extracted MediaStream from field=${field}`);
      }
      return { mediaStream: candidate, sourceField: field };
    }
  }

  const videoCaptureStream = source?.videoCaptureStream;
  const audioCaptureStream = source?.audioCaptureStream;
  if (isBrowserMediaStream(videoCaptureStream) || isBrowserMediaStream(audioCaptureStream)) {
    const tracks = [
      ...(videoCaptureStream?.getTracks?.() || []),
      ...(audioCaptureStream?.getTracks?.() || []),
    ];
    if (tracks.length > 0) {
      const composed = new MediaStream(tracks);
      if (scope === "local") {
        console.log(
          "[webZego] extracted MediaStream from field=videoCaptureStream+audioCaptureStream"
        );
      }
      return { mediaStream: composed, sourceField: "captureStreams" };
    }
  }

  const videoTracks = source?.getVideoTracks?.() || [];
  const audioTracks = source?.getAudioTracks?.() || [];
  if (videoTracks.length > 0 || audioTracks.length > 0) {
    const composed = new MediaStream([...videoTracks, ...audioTracks]);
    if (scope === "local") {
      console.log(
        `[webZego] extracted MediaStream from field=composeTracks video=${videoTracks.length} audio=${audioTracks.length}`
      );
    }
    return { mediaStream: composed, sourceField: "composeTracks" };
  }

  if (scope === "local") {
    console.log(`[webZego] local attach stream shape=${describeStreamShape(source)}`);
  }

  return { mediaStream: null, sourceField: null };
}

export function ensureCallVideoElement(container) {
  if (!container) return null;
  if (container.tagName === "VIDEO") return container;
  let video = container.querySelector("video[data-livora-zego-video]");
  if (!video) {
    video = document.createElement("video");
    video.dataset.livoraZegoVideo = "true";
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.style.position = "absolute";
    video.style.inset = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.background = "transparent";
    video.style.zIndex = "5";
    container.appendChild(video);
  }
  return video;
}

export function logCallVideoElementDiagnostics({ container, stream, scope = "remote" }) {
  const video =
    container?.tagName === "VIDEO"
      ? container
      : container?.querySelector?.("video[data-livora-zego-video]");
  const found = !!video;
  console.log(`[webZego] ${scope} video element found=${found}`);
  if (!video) {
    return { ok: false, video: null };
  }

  const activeStream = video.srcObject || stream || null;
  console.log(`[webZego] ${scope} video srcObject exists=${!!video.srcObject}`);

  const videoTracks = activeStream?.getVideoTracks?.() || [];
  const audioTracks = activeStream?.getAudioTracks?.() || [];
  console.log(
    `[webZego] ${scope} video tracks video=${videoTracks.length} audio=${audioTracks.length}`
  );

  const rect = video.getBoundingClientRect();
  const cs = typeof window !== "undefined" ? window.getComputedStyle(video) : null;
  const width = Math.round(rect.width || 0);
  const height = Math.round(rect.height || 0);
  console.log(
    `[webZego] ${scope} video layout width=${width} height=${height} display=${cs?.display || "n/a"} visibility=${cs?.visibility || "n/a"} opacity=${cs?.opacity || "n/a"} zIndex=${cs?.zIndex || "n/a"}`
  );
  console.log(
    `[webZego] ${scope} video parent class=${video.parentElement?.className || ""}`
  );

  return { ok: width > 0 && height > 0, video, width, height };
}

export async function attachStreamToCallVideoElement({
  container,
  stream,
  streamID = "local",
  scope = "remote",
  mirror = false,
}) {
  const video = ensureCallVideoElement(container);
  const found = !!video;
  console.log(`[webZego] ${scope} video element found=${found}`);
  if (!video || !stream) {
    if (!stream) {
      console.log(`[webZego] ${scope} attach skipped no stream streamID=${streamID}`);
    }
    return { ok: false, video: null };
  }

  const resolveScope = scope === "local" ? "local" : "remote";
  const { mediaStream } = resolveMediaStreamForVideoAttach(stream, resolveScope);

  if (scope === "local" && !mediaStream) {
    console.log("[webZego] local preview attach skipped: no MediaStream found");
    return { ok: false, video, skipped: true, reason: "no_media_stream" };
  }

  if (!mediaStream) {
    console.log(`[webZego] ${scope} attach skipped not MediaStream streamID=${streamID}`);
    return { ok: false, video, skipped: true, reason: "not_media_stream" };
  }

  if (container?.classList) {
    container.classList.add("callroom-has-direct-video");
  }

  try {
    video.srcObject = mediaStream;
    if (scope === "local") {
      console.log("[webZego] local video srcObject set success");
    }
  } catch (error) {
    console.log(
      `[webZego] ${scope} video srcObject set failed streamID=${streamID} error=${error?.message || error}`
    );
    if (scope === "local") {
      console.log("[webZego] local preview attach skipped: no MediaStream found");
    }
    return { ok: false, video, error, skipped: true };
  }

  video.autoplay = true;
  video.playsInline = true;
  video.muted = scope === "local";
  if (scope === "remote") {
    video.muted = false;
  }
  video.style.transform = mirror ? "scaleX(-1)" : "";

  try {
    await video.play();
    console.log(`[webZego] ${scope} video play success streamID=${streamID}`);
    logCallVideoElementDiagnostics({ container, stream: mediaStream, scope });
    return { ok: true, video, mediaStream };
  } catch (error) {
    console.log(
      `[webZego] ${scope} video play failed streamID=${streamID} error=${error?.message || error}`
    );
    logCallVideoElementDiagnostics({ container, stream: mediaStream, scope });
    return { ok: false, video, error, mediaStream };
  }
}

export function logStreamIdCheck({
  roomId,
  localUserId,
  localStreamId,
  remoteStreamId = "",
  remoteUserId = "",
}) {
  console.log(`[streamIdCheck] roomId=${roomId}`);
  console.log(`[streamIdCheck] localUserId=${localUserId}`);
  console.log(`[streamIdCheck] localStreamId=${localStreamId}`);
  if (remoteStreamId) console.log(`[streamIdCheck] remoteStreamId=${remoteStreamId}`);
  if (remoteUserId) console.log(`[streamIdCheck] remoteUserId=${remoteUserId}`);
}

export function parseRemoteUserIdFromStreamId(streamId, roomId) {
  if (!streamId || !roomId) return "";
  const prefix = `${roomId}_`;
  const suffix = "_main";
  const id = String(streamId);
  if (!id.startsWith(prefix) || !id.endsWith(suffix)) return "";
  return id.slice(prefix.length, -suffix.length);
}
